import React, { useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';

import { MenuButton, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, RadioGroup, Radio } from '@fluentui/react-components';
import { Stack } from '@fluentui/react';
import { AddRegular, ContentViewRegular, ImageRegular, VideoRegular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';

import { deleteAd } from '../model';
import { StepHeader } from '../../../shared/step-header';
import { useSelectedAdFormatState } from '../../../common/use-selected-ad-format';
import { AdThumbnail } from './ad-thumbnail';
import { STEP_PATHS, FLOWS, isAudienceAdGroupMultiAdCreation } from '../../../step-constants';
import { getAdsContainerStyles } from './styles';
import DesertSvg from '../../../media/desert.svg';

const iconSize = 20;

const FilterFormat = {
  all: {
    value: 'all',
    label: _TL_('All'),
  },
  native: {
    value: 'native',
    label: _TL_('Native ads'),
  },
  display: {
    value: 'display',
    label: _TL_('Display ads'),
  },
  video: {
    value: 'video',
    label: _TL_('Video ads'),
  },
};

export const MultiAdFormatAdsContainer = ({
  data,
  onDataChange,
}) => {
  const classes = useDefaultStyles(getAdsContainerStyles);
  const i18n = useContext(I18nContext);
  const { state, send } = useWizardService();
  const [, setSelectedAdFormat] = useSelectedAdFormatState();
  const [filterFormat, setFilterFormat] = useState(FilterFormat.all.value);

  const { ads } = (data && data.ads) || {};

  const {
    flow, campaign, adGroup, ads: adsData,
  } = state.context.wizardData;

  const adIds = useMemo(() => {
    switch (filterFormat) {
      case FilterFormat.native.value:
        return _.filter(_.keys(ads), adId => ads[adId].adSubType === AdFormat.Native);
      case FilterFormat.display.value:
        return _.filter(_.keys(ads), adId => ads[adId].adSubType === AdFormat.Display);
      case FilterFormat.video.value:
        return _.filter(_.keys(ads), adId => ads[adId].adSubType === AdFormat.Video);
      default:
        return _.keys(ads);
    }
  }, [ads, filterFormat]);

  const onClickCreateNewAd = useCallback((type) => {
    setSelectedAdFormat(type);

    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      editingAdId: null,
    });

    send(WIZARD_EVENTS.NAVIGATE_TO, {
      stepId: STEP_PATHS.MULTI_NATIVE_AD,
    });
  }, [send, setSelectedAdFormat]);

  const updateData = useCallback((adsToUpdate) => {
    onDataChange({
      data: {
        ...data,
        ads: {
          ...adsData,
          ads: {
            ...adsToUpdate,
          },
        },
      },
    });
  }, [adsData, data, onDataChange]);

  const onDeleteAdClicked = useCallback((adId) => {
    deleteAd({
      ad: [ads[adId].data],
      campaign,
      adGroup,
    }).then(() => {
      const adsWithoutDeletedAd = _.omit(ads, adId);

      if (_.isEmpty(adsWithoutDeletedAd)) {
        updateData(adsWithoutDeletedAd);

        const updatedFlow = isAudienceAdGroupMultiAdCreation(flow) ? FLOWS.AUDIENCE_AD_GROUP_CREATION : FLOWS.AUDIENCE_CAMPAIGNS;

        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          ads: {
            ...adsWithoutDeletedAd,
          },
          editingAdId: null,
          flow: updatedFlow,
        });

        send(WIZARD_EVENTS.SAVE_AND_GOTO, {
          stepId: STEP_PATHS.NATIVE_AD,
          flowFactors: { flow: updatedFlow },
        });
      } else {
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
          ads: {
            ...adsData,
            ads: {
              ...adsWithoutDeletedAd,
            },
          },
        });
        updateData(adsWithoutDeletedAd);
      }
    });
  }, [adGroup, ads, adsData, campaign, flow, send, updateData]);

  return (
    <div className="multi-ad-format-ads-container">
      <Stack tokens={{ childrenGap: 8 }}>
        <StepHeader
          title={_TL_('Ads')}
          description={_TL_('Create up to 15 ads for this campaign.')}
        />
      </Stack>
      <Stack className={classes.toolbar} horizontal verticalAlign="center" tokens={{ childrenGap: 10 }} wrap>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton className={classes.menuButton}>
              <AddRegular fontSize={iconSize} />
              {i18n.getString(_TL_('Create another ad'))}
            </MenuButton>
          </MenuTrigger>

          <MenuPopover className={classes.menuContainer}>
            <MenuList>
              <MenuItem onClick={() => onClickCreateNewAd(AdFormat.Native)}>
                <ContentViewRegular fontSize={iconSize} />
                {i18n.getString(_TL_('Native ad'))}
              </MenuItem>
              <MenuItem onClick={() => onClickCreateNewAd(AdFormat.Display)}>
                <ImageRegular fontSize={iconSize} />
                {i18n.getString(_TL_('Display ad'))}
              </MenuItem>
              <MenuItem onClick={() => onClickCreateNewAd(AdFormat.Video)}>
                <VideoRegular fontSize={iconSize} />
                {i18n.getString(_TL_('Video ad'))}
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <RadioGroup
          className={classes.radioGroup}
          onChange={(ev, change) => setFilterFormat(change.value)}
          value={filterFormat}
        >
          {_.keys(FilterFormat).map(key => (
            <Radio
              className={classNames(classes.radioOption, filterFormat === FilterFormat[key].value && 'selected')}
              key={key}
              value={FilterFormat[key].value}
              label={i18n.getString(FilterFormat[key].label)}
            />
          ))}
        </RadioGroup>
      </Stack>
      {
        data && !_.isEmpty(adIds) && (
          <Stack horizontal tokens={{ childrenGap: 15 }} wrap>
            {adIds.map(adId => (
              <AdThumbnail
                key={adId}
                item={ads[adId].data}
                onDeleteAdClicked={onDeleteAdClicked}
                type={ads[adId].adSubType}
              />
            ))}
          </Stack>
        )
      }
      {
        data && _.isEmpty(adIds) && (
          <Stack horizontal verticalAlign="center" horizontalAlign="center">
            <div className={classNames('empty-view-container', classes.emptyViewContainer)}>
              <img src={DesertSvg} alt="desert" />
              <div className="empty-view-title">{i18n.getString(_TL_('Nothing to see here'))}</div>
              <div className="empty-view-desc">
                {i18n.getString(_TL_("You haven't created any {{adType}} yet. Once you create an ad, you'll see it here."), { adType: i18n.getString(FilterFormat[filterFormat].label) })}
              </div>
            </div>
          </Stack>
        )
      }
    </div>
  );
};

MultiAdFormatAdsContainer.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
};
